/**
 * Essentials editor: surface Flow Validation messages stamped as schema.formatData.error.
 * Nested schema-object errors use PropertyPath (or Message breadcrumbs) to target leaves.
 */

import { reactive } from 'vue';

/** Per-host CompileError bags — keep off the Vue instance to avoid render-access warnings. */
const compileErrorBagsByHost = new WeakMap();

const FIELD_BREADCRUMB = /^object initializer field '([^']+)'\s*$/;
const ARRAY_BREADCRUMB = /^array initializer element (\d+)\s*$/;
const BREADCRUMB_SEP = '; in ';

/**
 * @param {object|null|undefined} schema
 * @returns {string|null}
 */
export function getSchemaCompilerError(schema) {
    const err = schema?.formatData?.error;
    return (typeof err === 'string' && err.trim()) ? err : null;
}

/**
 * Arg-level banner for mixed-stack editors that have no single Quasar input for the arg.
 * Skips when a nested leaf already carries formatData.error (leaf-only display).
 * For customForm with a full Errors map, prefer getCustomFormCompilerErrorBanner.
 * @param {string} editorKind  customForm | schemaObject | primitive | caption
 * @param {object|null|undefined} argSchema  schema.properties.Arguments.properties[argName]
 * @returns {string|null}
 */
export function getMixedStackArgCompilerError(editorKind, argSchema) {
    if (editorKind !== 'customForm' && editorKind !== 'schemaObject')
        return null;
    return getSchemaCompilerError(argSchema);
}

/**
 * Errors map passed into a custom form CompileError bag.
 * Full-bag Prompt (`_Arguments`) gets every ArgName; per-slot forms get only their ArgName
 * so interleaved scalars (e.g. Flag beside CaptureDTMF) do not banner on the nested form.
 * @param {object|null|undefined} validationErrors
 * @param {string} argName  editor argName (`_Arguments` or Settings / …)
 * @returns {object|null}
 */
export function errorsMapForCustomForm(validationErrors, argName) {
    if (!validationErrors || typeof validationErrors !== 'object')
        return null;
    if (argName === '_Arguments')
        return validationErrors;
    const err = validationErrors[argName];
    if (!err)
        return null;
    return { [argName]: err };
}

/**
 * Overlay banner for full-bag custom forms: first Errors[ArgName] message that has no
 * PropertyPath (scalar / unpathable). Leaf paths are stamped inside BasicForm.
 * @param {object|null|undefined} validationErrors  prop.Errors keyed by ArgName
 * @returns {string|null}
 */
export function getCustomFormCompilerErrorBanner(validationErrors) {
    if (!validationErrors || typeof validationErrors !== 'object')
        return null;
    for (const key of Object.keys(validationErrors)) {
        const err = validationErrors[key];
        if (!err || typeof err !== 'object')
            continue;
        const rawMessage = err.Message ?? err.message;
        if (!rawMessage || typeof rawMessage !== 'string')
            continue;
        const path = err.PropertyPath || err.propertyPath || tryParsePropertyPathFromMessage(rawMessage);
        if (path)
            continue;
        return stripCompilerBreadcrumbs(rawMessage);
    }
    return null;
}

/**
 * Join ArgName with a path relative to that arg (PropertyPath never includes ArgName).
 * @param {string} argName
 * @param {string|null|undefined} propertyPath
 * @returns {string}
 */
export function joinArgPropertyPath(argName, propertyPath) {
    if (!argName)
        return propertyPath || '';
    if (!propertyPath)
        return argName;
    if (propertyPath.startsWith('['))
        return argName + propertyPath;
    return `${argName}.${propertyPath}`;
}

/**
 * Quasar field props — only set when a message exists so we do not force error=false
 * and hide normal rule failures.
 * @param {string|null|undefined} message
 * @returns {{ error?: boolean, errorMessage?: string }}
 */
export function compilerErrorFieldProps(message) {
    if (!message)
        return {};
    return { error: true, errorMessage: message };
}

/**
 * Parse compiler Message breadcrumbs into a root-to-leaf PropertyPath
 * (same rules as ValidationErrorPath.TryParseFromMessage on the server).
 * @param {string|null|undefined} message
 * @returns {string|null}
 */
export function tryParsePropertyPathFromMessage(message) {
    if (!message || typeof message !== 'string')
        return null;

    const parts = message.split(BREADCRUMB_SEP);
    if (parts.length < 2)
        return null;

    const innerFirst = [];
    for (let i = 1; i < parts.length; i++) {
        const crumb = parts[i].trim();
        let m = FIELD_BREADCRUMB.exec(crumb);
        if (m) {
            innerFirst.push(m[1]);
            continue;
        }
        m = ARRAY_BREADCRUMB.exec(crumb);
        if (m) {
            innerFirst.push(`[${m[1]}]`);
            continue;
        }
        break;
    }

    if (!innerFirst.length)
        return null;

    innerFirst.reverse();
    let path = '';
    for (const seg of innerFirst) {
        if (seg.startsWith('['))
            path += seg;
        else
            path += (path ? '.' : '') + seg;
    }
    return path || null;
}

/**
 * Strip "; in object initializer field ..." / array breadcrumbs for display.
 * @param {string|null|undefined} message
 * @returns {string}
 */
export function stripCompilerBreadcrumbs(message) {
    if (!message || typeof message !== 'string')
        return message || '';
    const idx = message.indexOf(BREADCRUMB_SEP);
    if (idx < 0)
        return message;
    return message.slice(0, idx).trim() || message;
}

/**
 * Split PropertyPath into segments: "Items[0].Value" → ["Items", "[0]", "Value"]
 * @param {string} propertyPath
 * @returns {string[]}
 */
export function splitPropertyPath(propertyPath) {
    if (!propertyPath || typeof propertyPath !== 'string')
        return [];
    const segments = [];
    const re = /([^[.\]]+)|(\[\d+\])/g;
    let m;
    while ((m = re.exec(propertyPath)) !== null) {
        if (m[1])
            segments.push(m[1]);
        else if (m[2])
            segments.push(m[2]);
    }
    return segments;
}

/**
 * Walk schema along PropertyPath and stamp formatData.error on the leaf.
 * @param {object} rootSchema  arg schema (object or array)
 * @param {string} propertyPath
 * @param {string} message  display message (breadcrumbs already stripped preferred)
 * @returns {boolean} true if a nested leaf was stamped
 */
export function stampNestedCompilerError(rootSchema, propertyPath, message) {
    if (!rootSchema || !propertyPath || !message)
        return false;

    const segments = splitPropertyPath(propertyPath);
    if (!segments.length)
        return false;

    let node = rootSchema;
    for (const seg of segments) {
        if (!node || typeof node !== 'object')
            return false;

        if (seg.startsWith('[')) {
            const items = node.items;
            if (!items)
                return false;
            node = Array.isArray(items) ? items[0] : items;
            continue;
        }

        const props = node.properties;
        if (!props || !props[seg])
            return false;
        node = props[seg];
    }

    if (!node || typeof node !== 'object')
        return false;

    if (!node.formatData)
        node.formatData = {};
    node.formatData.error = message;
    return true;
}

/**
 * Apply a ValidationError onto an argument schema: nested leaf when path resolves,
 * otherwise arg-level formatData.error.
 * @param {object} argSchema
 * @param {{ Message?: string, PropertyPath?: string, message?: string, propertyPath?: string }} err
 * @returns {'nested'|'arg'|null}
 */
export function applyArgCompilerError(argSchema, err) {
    if (!argSchema || !err)
        return null;

    const rawMessage = err.Message ?? err.message;
    if (!rawMessage || typeof rawMessage !== 'string')
        return null;

    const path = err.PropertyPath || err.propertyPath || tryParsePropertyPathFromMessage(rawMessage);
    const display = stripCompilerBreadcrumbs(rawMessage);

    if (path && stampNestedCompilerError(argSchema, path, display)) {
        // Prefer leaf-only: clear arg-root error if present
        if (argSchema.formatData && argSchema.formatData.error)
            delete argSchema.formatData.error;
        return 'nested';
    }

    if (!argSchema.formatData)
        argSchema.formatData = {};
    argSchema.formatData.error = display;
    return 'arg';
}

/**
 * Remove formatData.error from schema and all nested properties/items (stale clear).
 * @param {object|null|undefined} schema
 */
export function clearNestedCompilerErrors(schema) {
    if (!schema || typeof schema !== 'object')
        return;

    if (schema.formatData && 'error' in schema.formatData)
        delete schema.formatData.error;

    const props = schema.properties;
    if (props && typeof props === 'object') {
        for (const key of Object.keys(props))
            clearNestedCompilerErrors(props[key]);
    }

    const items = schema.items;
    if (items) {
        if (Array.isArray(items)) {
            for (const item of items)
                clearNestedCompilerErrors(item);
        }
        else {
            clearNestedCompilerErrors(items);
        }
    }
}

/**
 * Sync a stable CompileError bag from a ValidationError-like object (or clear).
 * Mutates and returns the same bag reference for PlainWrapper stability.
 * @param {{ PropertyPath?: string|null, Message?: string|null }} bag
 * @param {{ Message?: string, PropertyPath?: string, message?: string, propertyPath?: string }|null|undefined} err
 * @returns {{ PropertyPath: string|null, Message: string|null }}
 */
export function syncCompileErrorBag(bag, err) {
    if (!bag || typeof bag !== 'object')
        return bag;

    if (!err) {
        bag.PropertyPath = null;
        bag.Message = null;
        return bag;
    }

    const rawMessage = err.Message ?? err.message;
    if (!rawMessage || typeof rawMessage !== 'string') {
        bag.PropertyPath = null;
        bag.Message = null;
        return bag;
    }

    bag.PropertyPath = err.PropertyPath || err.propertyPath || tryParsePropertyPathFromMessage(rawMessage) || null;
    bag.Message = stripCompilerBreadcrumbs(rawMessage);
    return bag;
}

/**
 * Apply a full prop.Errors map onto a form schema (full-bag custom forms).
 * For each ArgName: try ArgName[.PropertyPath], then PropertyPath alone (nested-only schema).
 * @param {object} formSchema  BasicForm schema root
 * @param {object|null|undefined} errorsMap  keyed by ArgName
 * @returns {{ stamped: number, unresolvedMessages: string[] }}
 */
export function applyErrorsMapToFormSchema(formSchema, errorsMap) {
    const unresolvedMessages = [];
    let stamped = 0;
    if (!formSchema || !errorsMap || typeof errorsMap !== 'object')
        return { stamped, unresolvedMessages };

    for (const argName of Object.keys(errorsMap)) {
        const err = errorsMap[argName];
        if (!err || typeof err !== 'object')
            continue;

        const rawMessage = err.Message ?? err.message;
        if (!rawMessage || typeof rawMessage !== 'string')
            continue;

        const relPath = err.PropertyPath || err.propertyPath || tryParsePropertyPathFromMessage(rawMessage) || null;
        const display = stripCompilerBreadcrumbs(rawMessage);
        const prefixed = joinArgPropertyPath(argName, relPath);

        if (prefixed && stampNestedCompilerError(formSchema, prefixed, display)) {
            stamped++;
            continue;
        }
        if (relPath && stampNestedCompilerError(formSchema, relPath, display)) {
            stamped++;
            continue;
        }
        // Scalar arg with no path: stamp onto form.properties[ArgName] when present.
        if (!relPath && stampNestedCompilerError(formSchema, argName, display)) {
            stamped++;
            continue;
        }

        unresolvedMessages.push(display);
    }

    return { stamped, unresolvedMessages };
}

/**
 * Sync full Errors map onto a stable bag for full-bag custom forms.
 * Clears single-entry PropertyPath/Message so BasicForm uses the map path.
 * @param {{ Errors?: object|null, PropertyPath?: string|null, Message?: string|null }} bag
 * @param {object|null|undefined} validationErrors
 */
export function syncCompileErrorErrorsMap(bag, validationErrors) {
    if (!bag || typeof bag !== 'object')
        return bag;

    bag.PropertyPath = null;
    bag.Message = null;

    if (!validationErrors || typeof validationErrors !== 'object' || !Object.keys(validationErrors).length) {
        bag.Errors = null;
        return bag;
    }

    // Shallow copy so watchers see a new object when the map identity is reused.
    const next = Object.create(null);
    for (const key of Object.keys(validationErrors)) {
        const err = validationErrors[key];
        if (!err || typeof err !== 'object')
            continue;
        next[key] = {
            Message: err.Message ?? err.message ?? null,
            PropertyPath: err.PropertyPath || err.propertyPath
                || tryParsePropertyPathFromMessage(err.Message ?? err.message) || null,
            ArgName: err.ArgName || key,
        };
    }
    bag.Errors = Object.keys(next).length ? next : null;
    return bag;
}

/**
 * Stable fingerprint of an Errors map for Vue $watch.
 * @param {object|null|undefined} bag
 * @returns {string}
 */
export function compileErrorBagWatchKey(bag) {
    if (!bag || typeof bag !== 'object')
        return '';
    if (bag.Errors && typeof bag.Errors === 'object') {
        const parts = [];
        for (const key of Object.keys(bag.Errors).sort()) {
            const e = bag.Errors[key];
            parts.push(`${key}\0${e?.PropertyPath ?? ''}\0${e?.Message ?? ''}`);
        }
        return `E:${parts.join('\n')}`;
    }
    return `${bag.PropertyPath ?? bag.propertyPath ?? ''}\0${bag.Message ?? bag.message ?? ''}`;
}

/**
 * Stable per-arg bag owned by the host (overlay / dialog).
 * Bags are Vue-reactive so BasicForm can watch PropertyPath/Message/Errors live.
 * Stored in a WeakMap (not on the instance) so first access during render does not
 * warn about undefined `_compileErrorBags`.
 * @param {object} host  component instance (or plain object in tests)
 * @param {string} argName
 * @returns {{ PropertyPath: string|null, Message: string|null, Errors?: object|null }}
 */
export function getCompileErrorBag(host, argName) {
    let bags = compileErrorBagsByHost.get(host);
    if (!bags) {
        bags = Object.create(null);
        compileErrorBagsByHost.set(host, bags);
    }
    if (!bags[argName])
        bags[argName] = reactive({ PropertyPath: null, Message: null, Errors: null });
    return bags[argName];
}
